"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { altFor } from "@/lib/portfolio-photos";

type PortfolioCat = { slug: string; label: string; images: string[] };

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Rotating "recent work" carousel shown in the right column of a service
 * page hero. Fetches portfolio images at runtime from /api/portfolio (so it
 * never depends on R2 being reachable at build time), matches the given
 * category, and cross-fades through them on a timer.
 *
 * Images use object-contain so the full photo is always shown, never
 * cropped or zoomed. The panel background fills any letterboxing.
 */
export default function WorkCarousel({
  category,
  serviceTitle,
}: {
  category: string;
  serviceTitle: string;
}) {
  const [images, setImages] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/portfolio")
      .then((r) => r.json())
      .then((cats: PortfolioCat[]) => {
        if (cancelled || !Array.isArray(cats)) return;
        // Match flexibly: R2 folders use a mix of old short names
        // ("trophies") and canonical names ("trophies & awards"), so a
        // bidirectional substring check covers both.
        const target = norm(category);
        const match = cats.find((c) => {
          const cs = norm(c.slug);
          const cl = norm(c.label);
          return (
            cs === target ||
            cl === target ||
            cs.includes(target) ||
            target.includes(cs)
          );
        });
        setImages(match ? match.images.slice(0, 10) : []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [category]);

  const count = images.length;
  const go = useCallback(
    (n: number) => setIndex((i) => (i + n + count) % count),
    [count],
  );

  // Auto-rotate while there is more than one image.
  useEffect(() => {
    if (count < 2) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), 4500);
    return () => clearInterval(t);
  }, [count]);

  // Once we have confirmed the category has no images, render nothing so
  // the hero collapses cleanly to a single column.
  if (loaded && count === 0) return null;

  return (
    <div className="relative aspect-[4/5] w-full bg-white/5 overflow-hidden">
      {images.map((url, i) => (
        <Image
          key={url}
          src={url}
          alt={altFor(url, serviceTitle)}
          fill
          sizes="(max-width: 1024px) 100vw, 600px"
          className={`object-contain transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
          unoptimized
          priority={i === 0}
        />
      ))}

      {count > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Previous photo"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/55 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Next photo"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/55 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
            {images.map((url, i) => (
              <button
                key={url}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Go to photo ${i + 1}`}
                className={`h-1.5 transition-all duration-200 ${
                  i === index ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
